import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh,
} from '@mui/icons-material';

import { MetricsCards } from '@/components/dashboard/MetricsCards';
import { ActivityFeed } from '@/components/dashboard/ActivityFeed';
import { InteractiveMap } from '@/components/dashboard/InteractiveMap';
import { PreTripReadiness } from '@/components/dashboard/PreTripReadiness';
import { SLAAlerts } from '@/components/dashboard/SLAAlerts';
import { QuickActions } from '@/components/dashboard/QuickActions';
import MobileActivityFeed from '@/components/dashboard/MobileActivityFeed';

// New Analytics Components
import DashboardSummary from '@/components/dashboard/DashboardSummary';
import BookingTrendsChart from '@/components/dashboard/BookingTrendsChart';
import RecentBookingsTable from '@/components/dashboard/RecentBookingsTable';
import { simplBookingService } from '@/services/bookingService';
import DateRangeFilter, { getDateRange } from '@/components/common/DateRangeFilter';
import type { DateRange } from '@/components/common/DateRangeFilter';

export const DashboardPage: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRange>(() => getDateRange('this_month'));
  const [refreshing, setRefreshing] = React.useState(false);

  // New analytics states
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeTab, setActiveTab] = useState(0);
  const [bookings, setBookings] = useState<any[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setRefreshing(false);
  };

  // Handle category changes and filtering
  const handleCategoryChange = async (category: string) => {
    setActiveCategory(category);
    setBookingsLoading(true);

    try {
      const filtered = await simplBookingService.getBookingsByCategory(category);
      setBookings(filtered);
    } catch (error) {
      console.error(`Failed to filter ${category} bookings:`, error);
      setBookings([]);
    } finally {
      setBookingsLoading(false);
    }
  };

  // Initial load
  React.useEffect(() => {
    handleCategoryChange('all');
  }, []);


  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back! Here's what's happening with your platform today.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <DateRangeFilter
            value={dateRange}
            onChange={setDateRange}
            defaultPreset="this_month"
          />

          <IconButton
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{
              animation: refreshing ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          >
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {/* Tabs for different dashboard views */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Analytics Overview" />
          <Tab label="Live Operations" />
          <Tab label="Latest Bookings" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 ? (
        <>
          {/* Analytics Dashboard */}
          {/* Analytics Summary Cards */}
          <DashboardSummary onCategoryClick={handleCategoryChange} />

          {/* Booking Trends Chart */}
          <BookingTrendsChart startDate={dateRange.startDate} endDate={dateRange.endDate} />
        </>
      ) : activeTab === 2 ? (
        <>
          {/* Latest Bookings Table */}
          <RecentBookingsTable
            bookings={bookings}
            bookingsLoading={bookingsLoading}
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
            onRefresh={() => handleCategoryChange(activeCategory)}
          />
        </>
      ) : (
        <>
          {/* Live Operations Dashboard */}
          <Grid container spacing={3}>
            {/* Original Metrics Cards */}
            <Grid item xs={12}>
              <MetricsCards timeRange="this_month" />
            </Grid>

        {/* Interactive Map */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: 400 }}>
            <CardContent sx={{ height: '100%', p: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Active Trips & Driver Locations
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    label="42 Active Trips"
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    label="28 Drivers Online"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                  <Chip
                    label="156 Mobile Users"
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                  <Chip
                    label="23 Booking Sessions"
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                </Box>
              </Box>
              <InteractiveMap />
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} lg={4}>
          <QuickActions />
        </Grid>

        {/* Mobile Activity Feed - NEW */}
        <Grid item xs={12} lg={6}>
          <MobileActivityFeed />
        </Grid>

        {/* Activity Feed */}
        <Grid item xs={12} lg={6}>
          <ActivityFeed />
        </Grid>

        {/* Pre-Trip Readiness */}
        <Grid item xs={12} lg={6}>
          <PreTripReadiness />
        </Grid>

            {/* SLA Alerts */}
            <Grid item xs={12}>
              <SLAAlerts />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};